import { bindEmailSend } from './email-send'
import { copyText } from './copied'
import { invoiceBreakdown } from './invoice-breakdown'
import type { BreakdownVisit } from './invoice-breakdown'
import { toCollect } from './brought'
import { api, apiBlob, ApiError, changeNow, mutate, readCache } from './api'
import { sheet } from './day-close'
import { getMe } from './me'
import { onAPhone, whatsappHref } from './rebook-message'
import { firstName, smsHref } from './late-message'
import { clear, el, money, toast } from './ui'
import { invoicesOn } from './business-settings'
import { invoiceAfterEachJob, isCommercial, type CommercialCustomer } from './commercial'
import { textsReach, wayFor } from './contact'
import { EMAIL_RULE } from './mailto'
import { screenLocale } from './round-config'
import { undoPatch, type UndoLink } from './undo'
import './invoices.css'

// Invoices to customers (0065; Liam, 12 Oct 2026; docs/plans/customer-invoices/PLAN.md). The owner's phone only. An
// invoice is made at the moment it is sent and never changes afterwards; paid is worked out from its visits. Nothing goes
// to a customer until the owner taps Send.

export interface InvoiceLine { clean_id: number; date: string; label: string; pence: number; count?: number; each_pence?: number }
export interface InvoiceParty { name: string; address?: string | null; contact?: string | null; email?: string | null; po?: string | null; bank?: string | null; vat_number?: string | null; for_customer?: string | null; sym?: string }
export type InvoiceState = 'paid' | 'part_paid' | 'sent' | 'not_sent' | 'late' | 'cancelled' | 'credit'
export interface Invoice {
  id: number
  customer_id: number
  number: string
  kind: 'visit' | 'month' | 'credit'
  period: string | null
  issued_on: string
  due_on: string | null
  subtotal_pence: number
  less_pence: number
  total_pence: number
  vat_rate_bp: number | null
  vat_pence: number | null
  from: InvoiceParty
  to: InvoiceParty
  lines: InvoiceLine[]
  has_logo: boolean
  state: InvoiceState
  remaining_pence?: number
  part_paid?: boolean
  sent_at: string | null
  sent_via: string | null
  cancelled_at: string | null
  credited_by: string | null
  credit_for_id: number | null
  reminders: number
  version: number
}
interface EmailReady { ok: boolean; to: string | null; why: string | null }
interface Answer { row: Invoice; link: string | null; words: { invoice: string; reminder: string; receipt: string } | null; pdf_words?: { invoice: string; reminder: string; receipt: string }; email: EmailReady }
interface Made { row: Invoice; link: string; words: string; pdf_words?: string; email_error: string | null }
type Draft = Omit<Invoice, 'id' | 'state' | 'sent_at' | 'sent_via' | 'cancelled_at' | 'credited_by' | 'credit_for_id' | 'reminders' | 'version' | 'has_logo'> & {
  // Their mobile as it is now, and each visit with its version: what a send carries back (AGREED.md 4).
  breakdown_visits?: BreakdownVisit[]
  breakdown_prices?: Partial<Record<'windows' | 'roof' | 'gutters' | 'conservatory', number>>
  phone?: string | null
  visits?: { id: number; version: number; payment_status: string }[]
}

export const STATE_WORDS: Record<InvoiceState, string> = {
  paid: 'Paid', part_paid: 'Part paid', sent: 'Sent', not_sent: 'Not sent yet', late: 'Late', cancelled: 'Cancelled', credit: 'Credit note',
}
const isOwner = (): boolean => getMe()?.role === 'owner'
export const invoicesHere = (): boolean => isOwner() && invoicesOn()
const signed = (p: number): string => (p < 0 ? `-${money(-p)}` : money(p))
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
export const monthName = (period: string): string => `${MONTHS[Number(period.slice(5, 7)) - 1] ?? period}${period.slice(0, 4) === new Date().toISOString().slice(0, 4) ? '' : ` ${period.slice(0, 4)}`}`
// In the round's own words: "20 Oct 2026", or "Oct 20, 2026" on a US round (Astra C16, Sol C22; AGREED.md 16).
const dateWords = (iso: string): string => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(screenLocale(), { day: 'numeric', month: 'short', year: 'numeric' })

export function stateChip(state: InvoiceState): HTMLElement {
  return el('span', { class: `invoice-chip invoice-chip-${state}` }, STATE_WORDS[state])
}

// The invoice as the customer will see it (the link's page says the same).
export function customerInvoiceView(inv: Draft & { id?: number; has_logo?: boolean; state?: InvoiceState }, logoSrc: string | null): HTMLElement {
  const quantities = inv.lines.some(l => l.count !== undefined || l.each_pence !== undefined)
  const credit = inv.kind === 'credit'
  const paid = inv.state === 'paid' || (!credit && inv.total_pence <= 0)
  const title = credit ? 'Credit note' : paid ? 'Receipt' : 'Invoice'
  // No logo picked, no logo: the first letter of the name in a square read as one he never chose (Liam, 26 Oct 2026).
  const logo = logoSrc ? [el('img', { src: logoSrc, alt: `${inv.from.name} logo`, class: 'invoice-logo' })] : []
  const meta = [`${inv.number} · ${dateWords(inv.issued_on)}`]
  if (inv.kind === 'month' && inv.period) meta.push(monthName(inv.period))
  if (!credit && !paid && inv.due_on && inv.state !== 'cancelled') meta.push(`Pay by ${dateWords(inv.due_on)}`)
  const party = (label: string, p: InvoiceParty, extra: string[]): HTMLElement => el('p', { class: 'invoice-party' },
    el('span', { class: 'muted' }, label), el('br'), el('strong', {}, p.name),
    ...extra.filter(Boolean).flatMap((x) => [el('br'), x]))
  const art = el('article', { class: 'invoice customer-invoice' },
    ...(inv.state === 'cancelled' ? [el('span', { class: 'invoice-stamp invoice-stamp-off' }, 'CANCELLED')] : paid ? [el('span', { class: 'invoice-stamp' }, 'PAID')] : inv.state === 'part_paid' ? [el('span', { class: 'invoice-stamp' }, 'PART PAID')] : []),
    el('div', { class: 'invoice-top' }, ...logo, el('div', { class: 'invoice-title' }, el('strong', {}, title), ...meta.map((m) => el('span', {}, m)))),
    party('From', inv.from, [inv.from.address ?? '', inv.from.contact ?? '', inv.from.vat_number ? `VAT number ${inv.from.vat_number}` : '']),
    party('To', inv.to, [inv.to.for_customer ? `For ${inv.to.for_customer}` : '', inv.to.address ?? '', inv.to.po ? `Order number ${inv.to.po}` : '']),
    el('div', { class: `invoice-lines${quantities ? ' invoice-quantities' : ''}` },
      ...(quantities ? [el('div', { class: 'invoice-line invoice-line-head' }, el('span', {}, 'What'), el('span', {}, 'How many'), el('span', {}, 'Each'), el('span', {}, 'Price'))] : []),
      ...inv.lines.map((l) => el('div', { class: 'invoice-line' }, el('span', {}, l.label), ...(quantities ? [el('span', {}, l.count == null ? '' : String(l.count)), el('span', {}, l.each_pence == null ? '' : signed(l.each_pence))] : []), el('strong', {}, signed(l.pence))))),
    ...(inv.less_pence > 0 ? [el('div', { class: 'invoice-sum' }, el('span', {}, 'Already paid or credit'), el('span', {}, `-${money(inv.less_pence)}`))] : []),
    ...(inv.vat_pence != null ? [el('div', { class: 'invoice-sum' }, el('span', {}, `Includes VAT at ${(inv.vat_rate_bp ?? 0) / 100}%`), el('span', {}, signed(inv.vat_pence)))] : []),
    el('div', { class: 'invoice-total' }, el('span', {}, credit ? 'Credit' : paid ? 'Paid' : 'To pay'), el('span', {}, signed(paid && !credit ? inv.subtotal_pence : inv.total_pence))),
    // Part paid, late, or not sent yet with money put on it since (the recheck's G1): what is still to pay.
    ...(inv.state === 'part_paid' || ((inv as { part_paid?: boolean }).part_paid && (inv.state === 'late' || inv.state === 'not_sent')) ? [el('div', { class: 'invoice-sum' }, el('span', {}, 'Still to pay'), el('strong', {}, money((inv as { remaining_pence?: number }).remaining_pence ?? inv.total_pence)))] : []),
    ...(!credit && !paid && inv.state !== 'cancelled' && inv.from.bank?.trim() ? [el('p', { class: 'invoice-pay' }, el('strong', {}, 'Pay by bank'), el('br'), inv.from.bank, el('br'), `Reference: ${inv.number}`)] : []))
  return art
}

// WhatsApp or a text with the words written, as a link the owner taps (the phone's own app sends it).
function appHref(way: 'whatsapp' | 'sms', phone: string | null, words: string): string | null {
  return way === 'whatsapp' ? whatsappHref(phone, words, onAPhone()) : smsHref(phone, words, navigator.userAgent)
}
async function phoneOf(customerId: number, phone?: string | null): Promise<string | null> {
  if (phone !== undefined) return phone
  try { return (await api<{ customer: { phone: string | null } }>(`/api/customers/${customerId}`)).customer?.phone ?? null } catch { return null }
}
const changed = (): void => { window.dispatchEvent(new CustomEvent('dropby:data-changed')) }
const failure = (e: unknown): string => (e instanceof ApiError || e instanceof Error ? e.message : 'It did not go. Try again.')

// ---- A new invoice: check it, then send ---------------------------------------------------------------------------
export interface InvoiceAsk { cleanIds?: number[]; month?: string; dayOf?: number }
export function openNewInvoice(customerId: number, ask: InvoiceAsk, opts: { phone?: string | null; done?: () => void } = {}): void {
  const s = sheet('New invoice')
  s.head.append(el('h2', {}, ask.month ? `${monthName(ask.month)}'s invoice` : 'Invoice'))
  renderInvoiceStep(s.body, customerId, ask, { done: opts.done, close: s.close })
}

// Extra features' Invoices set-up, from a screen that is not allowed to link there itself (More > Invoices: the
// extra-features check lists the files that do). The open sheet goes first.
// Straight to the Invoices switch on Extra features, not the top of the page (Liam, 23 Oct 2026: "the flow for set up
// invoices is confusing takes us here", the page opening on Several jobs with its switch glowing).
export const INVOICE_SET_UP = '#/extra-features?show=invoices'
export function openInvoiceSetUp(close?: () => void): void { close?.(); window.location.hash = INVOICE_SET_UP }

// What can go on an invoice for a customer now: the server's one rule (functions/lib/invoices.ts readyFor), read by
// More > Invoices' New invoice and their page's Invoices card alike (docs/plans/invoice-maker/PLAN.md, Astra r2 3).
export interface ReadyVisit { id: number; version: number; date: string; amount_pence: number; less_pence: number; payment_status: string; label: string; was_on: string | null }
export interface ReadyMonth { period: string; visits: number; pence: number; to_pay_pence: number; over: boolean }
export interface Ready { monthly: boolean; visits: ReadyVisit[]; months: ReadyMonth[] }
export const readyToInvoice = (customerId: number): Promise<Ready> => api<Ready>(`/api/invoices?ready=${customerId}`)

// The server's own words for a paused or view-only round (functions/lib/access-words.ts), so an invoice offers no send
// the server would refuse (AGREED.md 7). Pinned equal to the server's by scripts/commercial-invoices-server.test.mjs.
export const PAUSED_SEND_WORDS = "Your round is paused, so sending messages from Roundly is off until it's open again."
export const VIEW_ONLY_SEND_WORDS = "Your round is view and download only, so nothing can be changed until it's open again."
export function cannotSend(): string | null {
  const a = getMe()?.access?.access
  return a === 'paused' ? PAUSED_SEND_WORDS : a === 'view_only' ? VIEW_ONLY_SEND_WORDS : null
}

type Way = 'email' | 'sms' | 'whatsapp' | 'link'
const WAY_LABEL: Record<Way, string> = { email: 'Email', sms: 'Text', whatsapp: 'WhatsApp', link: 'Open to save or share' }
interface Seen { to: unknown; phone: string | null; visits: { id: number; version: number }[] }
interface Preview { draft: Draft; email: EmailReady; seen?: Seen }
interface StepCustomer { id: number; name: string; version: number; phone?: string | null; email?: string | null; message_by?: string | null; invoice_to_email?: string | null; commercial?: number | null; invoice_mode?: string | null }
export interface StepOptions {
  done?: () => void
  // The sheet or the pop-up goes.
  close: () => void
  // Made (numbered), sent or not: the Done pop-up stops offering "Not done after all" (AGREED.md 6).
  onMade?: (row: Invoice) => void
  onBreakdownSaved?: (owedPence: number) => void
  onVisitSaved?: (visit: { id: number; version: number }) => void
}

// The invoice ready to send, checked, with what was seen kept for the send (AGREED.md 3, 4, 6, 7, 12, 13), drawn into a
// sheet of its own (openNewInvoice: the visit's details, their page, Owed, Today's bar) or into the Done pop-up.
// Their way first (Text for a customer who is texted or set to Don't message, WhatsApp, Email when it can go), then the
// others, then Open to save or share. Nothing an invoice can go to: said, with "+ Add a number".
export function renderInvoiceStep(host: HTMLElement, customerId: number, ask: InvoiceAsk, opts: StepOptions): void {
  host.classList.add('invoice-step')
  const body = { customer_id: customerId, ...(ask.cleanIds ? { clean_ids: ask.cleanIds } : {}), ...(ask.month ? { month: ask.month } : {}), ...(ask.dayOf ? { day_of: ask.dayOf } : {}) }
  // A send whose answer never came goes again under the same key, so the server answers it as it did: one invoice,
  // never two (AGREED.md 6). Any answer, and the next send is a new one.
  let sendKey: { via: Way; key: string } | null = null
  let customer: StepCustomer | null = null
  let breakdown: ReturnType<typeof invoiceBreakdown> | null = null

  const load = (notice?: string, saved = false): void => {
    clear(host)
    breakdown = null
    host.classList.remove('invoice-breakdown-open')
    const stop = cannotSend()
    if (stop) { host.append(el('p', { class: 'invoice-note invoice-paused' }, stop)); return }
    host.append(el('p', { class: 'muted' }, 'Getting it ready…'))
    void Promise.all([
      changeNow<Preview>('POST', '/api/invoices', { ...body, preview: true }),
      api<{ customer: StepCustomer }>(`/api/customers/${customerId}`).then((r) => r.customer).catch(() => null),
    ]).then(([p, c]) => { customer = c ?? customer; draw(p, notice, saved) }).catch((e) => refused(e))
  }

  const refused = (e: unknown): void => {
    clear(host)
    // Already on an invoice (a lost answer, another phone): that one is opened, never a second.
    const on = e instanceof ApiError ? (e.body?.invoice as { id?: number; number?: string } | undefined) : undefined
    if (e instanceof ApiError && e.code === 'on_invoice' && on?.id) {
      host.append(el('p', {}, failure(e)))
      const open = el('button', { type: 'button', class: 'btn-primary' }, `Open ${on.number ?? 'the invoice'}`)
      open.addEventListener('click', () => { opts.close(); openInvoice(on.id!, { done: opts.done }) })
      host.append(el('div', { class: 'invoice-actions' }, open))
      return
    }
    const setUp = e instanceof ApiError && e.code === 'invoice_setup_missing'
    // No signal: where it waits, and Try again (the recheck's S4).
    if (e instanceof ApiError && e.status === 0) {
      const again = el('button', { type: 'button', class: 'btn-secondary' }, 'Try again')
      again.addEventListener('click', () => load())
      host.append(el('p', { class: 'invoice-note' }, ask.dayOf ? 'No signal. It waits in Owed under "Invoice to send".' : 'No signal. Try again when you have signal.'), el('div', { class: 'invoice-actions' }, again))
      return
    }
    host.append(el('p', { class: setUp ? 'invoice-note invoice-setup-missing' : 'invoice-note' }, failure(e)))
    // A way forward (Astra, judging the pictures for Liam, 21 Oct 2026): Extra features, where Invoices is set up.
    if (setUp) {
      const go = el('a', { href: INVOICE_SET_UP, class: 'btn-primary invoice-go' }, 'Set up invoices')
      go.addEventListener('click', () => opts.close())
      host.append(go)
    }
  }

  const draw = (p: Preview, notice?: string, saved = false): void => {
    clear(host)
    const draft = p.draft
    const seen: Seen = p.seen ?? { to: draft.to, phone: draft.phone ?? null, visits: (draft.visits ?? []).map((v) => ({ id: v.id, version: v.version })) }
    if (notice) host.append(el('p', { class: saved ? 'invoice-note' : 'form-error', role: saved ? 'status' : 'alert' }, notice))
    let preview = customerInvoiceView({ ...draft, state: undefined }, null)
    host.append(preview)
    // Design A (Liam, 26 Oct 2026: "Invoices just print as a url that looks spammy", then "I actualy prefer A"): Send PDF
    // first, then Email it and Download PDF, the note that goes with it, and the links folded under "Send a link instead".
    host.insertBefore(pdfWays(p, draft, seen), preview)
    if (invoicesHere() && draft.breakdown_visits?.length) {
      const visits = draft.breakdown_visits
      breakdown = invoiceBreakdown(visits, draft.breakdown_prices ?? {}, {
        onPreview: (edited) => {
          const lines = visits.flatMap(v => edited.has(v.id) ? edited.get(v.id)!.length ? edited.get(v.id)!.map(l => ({ clean_id: v.id, date: v.date, label: `${l.name}, ${v.label}`, count: l.count, each_pence: l.each_pence, pence: l.count * l.each_pence })) : v.plain_lines : draft.lines.filter(l => l.clean_id === v.id))
          const subtotal = lines.reduce((s, l) => s + l.pence, 0)
          const less = visits.reduce((s, v) => s + Math.min(v.paid_pence, lines.filter(l => l.clean_id === v.id).reduce((n, l) => n + l.pence, 0)), 0)
          const next = customerInvoiceView({ ...draft, lines, subtotal_pence: subtotal, less_pence: less, total_pence: subtotal - less,
            vat_pence: draft.vat_rate_bp == null ? null : Math.round(subtotal * draft.vat_rate_bp / (10_000 + draft.vat_rate_bp)), state: undefined }, null)
          preview.replaceWith(next); preview = next
        },
        onVisitSaved: opts.onVisitSaved,
        onSaved: () => { changed(); load('Breakdown saved. Check the invoice and send it.', true) },
        onLocked: refused,
      })
      host.insertBefore(breakdown.node, host.firstChild)
      if (saved) opts.onBreakdownSaved?.(draft.total_pence)
    }
    const linkWays = (inner: HTMLElement): HTMLElement => el('details', { class: 'invoice-link-ways' }, el('summary', {}, 'Send a link instead'), inner)
    const phone = seen.phone
    const reach = { text: textsReach(phone), email: EMAIL_RULE.test(String(draft.to.email ?? '').trim()) }
    const can: Record<Way, boolean> = { email: p.email.ok, sms: reach.text, whatsapp: !!phone && whatsappHref(phone, 'x', onAPhone()) !== null, link: true }
    const theirs = wayFor({ message_by: customer?.message_by ?? null })
    const first: Way = theirs === 'email' ? 'email' : theirs === 'whatsapp' ? 'whatsapp' : 'sms'
    const ways: Way[] = []
    for (const w of [first, 'sms', 'whatsapp', 'email', 'link'] as Way[]) if (can[w] && !ways.includes(w)) ways.push(w)
    // Not a commercial customer invoiced each visit: the sheet exactly as before (the recheck's S1): Send by email or why
    // not, then WhatsApp, Text and Open to save or share, always, WhatsApp in lemon without email.
    if (!invoiceAfterEachJob(customer)) {
      const classic = el('div', { class: 'invoice-actions invoice-actions-first' })
      if (!p.email.ok && p.email.why) classic.append(el('p', { class: 'muted invoice-note' }, p.email.why))
      const grid = el('div', { class: 'invoice-grid' })
      for (const [via, label] of [['whatsapp', 'WhatsApp'], ['sms', 'Text'], ['link', 'Open to save or share']] as const) {
        const b = el('button', { type: 'button', class: `btn-secondary${!p.email.ok && via === 'whatsapp' ? ' invoice-lemon' : ''}` }, label) as HTMLButtonElement
        b.addEventListener('click', () => void send(via, b, draft, seen))
        grid.append(b)
      }
      classic.append(linkWays(grid), el('p', { class: 'muted invoice-note' }, `It will be numbered ${draft.number}. Once sent it can't be taken back: to change it later, cancel it and send a new one.`))
      host.insertBefore(classic, preview)
      return
    }
    const actions = el('div', { class: 'invoice-actions' })
    if (!reach.text && !reach.email) {
      actions.append(el('p', { class: 'invoice-noreach' }, 'No mobile or email for them'))
      const add = el('button', { type: 'button', class: 'btn-secondary' }, '+ Add a number')
      add.addEventListener('click', () => void addNumber())
      actions.append(add)
    } else if (!p.email.ok && reach.email && !reach.text && p.email.why) actions.append(el('p', { class: 'muted invoice-note' }, p.email.why))
    // Email is its own row above (Email it from Roundly); the links, their way first, under "Send a link instead".
    const row = el('div', { class: 'invoice-ways' })
    for (const w of ways.filter((x) => x !== 'email')) {
      const b = el('button', { type: 'button', class: 'btn-secondary' }, WAY_LABEL[w]) as HTMLButtonElement
      b.addEventListener('click', () => void send(w, b, draft, seen))
      row.append(b)
    }
    actions.append(linkWays(row))
    // After Done: what it holds is the jobs done so far that day; one finished later gets its own (Sol C08).
    if (ask.dayOf) actions.append(el('p', { class: 'muted invoice-note' }, 'For their jobs done so far that day. A job done later gets its own invoice.'))
    actions.append(el('p', { class: 'muted invoice-note' }, `It will be numbered ${draft.number}. Once sent it can't be taken back: to change it later, cancel it and send a new one.`))
    // The ways to send first, the invoice under them (Astra, judging the pictures for Liam, 21 Oct 2026).
    actions.classList.add('invoice-actions-first')
    host.insertBefore(actions, preview)
  }

  // ---- As a PDF (design A) ----------------------------------------------------------------------------------------
  // The note that goes with it, his to change before it goes; the server's own words replace it once the invoice is
  // made, unless he has changed it.
  let note: HTMLTextAreaElement | null = null
  let noteTouched = false
  const pdfWays = (p: Preview, draft: Draft, seen: Seen): HTMLElement => {
    const box = el('div', { class: 'invoice-pdf' })
    const sendPdf = el('button', { type: 'button', class: 'btn-primary invoice-pdf-send' }, 'Send PDF') as HTMLButtonElement
    sendPdf.addEventListener('click', () => void makePdf('share', sendPdf, draft, seen))
    box.append(sendPdf, el('p', { class: 'muted invoice-note invoice-pdf-hint' }, pdfHint()))
    const rows = el('div', { class: 'invoice-pdf-rows' })
    const rowButton = (title: string, line: string, act: (b: HTMLButtonElement) => void): void => {
      const b = el('button', { type: 'button', class: 'invoice-pdf-row' }, el('strong', {}, title), el('small', {}, line)) as HTMLButtonElement
      b.addEventListener('click', () => act(b))
      rows.append(b)
    }
    if (p.email.ok) rowButton('Email it from Roundly', `To ${p.email.to}`, (b) => void send('email', b, draft, seen))
    rowButton('Download PDF', 'Save it and send it your own way', (b) => void makePdf('download', b, draft, seen))
    box.append(rows)
    const due = draft.due_on ? `, to pay by ${dateWords(draft.due_on)}` : ''
    note = el('textarea', { class: 'invoice-pdf-note', rows: '4', 'aria-label': 'The note with it' }) as HTMLTextAreaElement
    note.value = `Hi ${firstName(draft.to.for_customer || draft.to.name) || 'there'}, here's your invoice ${draft.number} for ${money(draft.total_pence)}${due}.\nPlease use ${draft.number} as the reference.\n${draft.from.name}`
    note.addEventListener('input', () => { noteTouched = true })
    box.append(el('label', { class: 'invoice-pdf-note-label' }, el('span', {}, 'The note with it'), note))
    return box
  }

  // Made (numbered, as any way to send it does), then its PDF, then a second tap shares or saves it: the phone opens its
  // share menu only straight from a tap (Astra's PDF check 2). Sent once he says so: sharing, or saving, is not sending
  // (check 1). Made but not fetched: Try again fetches the same invoice, never makes another (check 6).
  const makePdf = async (mode: 'share' | 'download', button: HTMLButtonElement, draft: Draft, seen: Seen): Promise<void> => {
    if (breakdown?.dirty() || breakdown?.busy()) { toast('Save the breakdown before making the invoice.'); return }
    for (const b of host.querySelectorAll('button')) (b as HTMLButtonElement).disabled = true
    const was = button.textContent
    button.textContent = 'Making it…'
    if (!sendKey || sendKey.via !== 'link') sendKey = { via: 'link', key: crypto.randomUUID() }
    try {
      const made = await changeNow<Made>('POST', '/api/invoices', { ...body, via: 'link', expect_total: draft.total_pence, expect_to: seen.to, expect_phone: seen.phone, expect_visits: seen.visits }, sendKey.key)
      sendKey = null
      host.classList.remove('invoice-breakdown-open')
      opts.onMade?.(made.row)
      changed()
      opts.done?.()
      // An edited note keeps his words, with the number the invoice was actually given (Sol's PDF review 1).
      const words = noteTouched && note ? note.value.split(draft.number).join(made.row.number) : made.pdf_words ?? note?.value ?? ''
      void pdfStep(made, mode, words, seen.phone)
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 0)) sendKey = null
      if (e instanceof ApiError && e.code === 'changed') { load(failure(e)); return }
      if (e instanceof ApiError && (e.code === 'on_invoice' || e.code === 'invoice_setup_missing')) { refused(e); return }
      toast(failure(e))
      button.textContent = was
      for (const b of host.querySelectorAll('button')) (b as HTMLButtonElement).disabled = false
    }
  }

  const pdfStep = (made: Made, mode: 'share' | 'download', words: string, phone: string | null): Promise<void> =>
    pdfFlow(host, made.row, words, mode, { close: opts.close, done: opts.done, askSent: true, phone })

  // The add box, saving a mobile or an email for invoices without changing how they are messaged (build-messages,
  // src/add-details.ts, AGREED.md 12).
  const addNumber = async (): Promise<void> => {
    try { customer = (await api<{ customer: StepCustomer }>(`/api/customers/${customerId}`)).customer ?? customer } catch { /* the one read before */ }
    if (!customer) { toast('Their details need signal to change.'); return }
    const { openAddDetails } = await import('./add-details')
    openAddDetails(customer as unknown as Parameters<typeof openAddDetails>[0], { invoice: true, need: { number: true }, onSaved: () => load() })
  }

  const send = async (via: Way, button: HTMLButtonElement, draft: Draft, seen: Seen): Promise<void> => {
    if (breakdown?.dirty() || breakdown?.busy()) { toast('Save the breakdown before making the invoice.'); return }
    for (const b of host.querySelectorAll('button')) (b as HTMLButtonElement).disabled = true
    const was = button.textContent
    button.textContent = via === 'email' ? 'Sending…' : 'Making it…'
    if (!sendKey || sendKey.via !== via) sendKey = { via, key: crypto.randomUUID() }
    try {
      const made = await changeNow<Made>('POST', '/api/invoices', { ...body, via, expect_total: draft.total_pence, expect_to: seen.to, expect_phone: seen.phone, expect_visits: seen.visits }, sendKey.key)
      sendKey = null
      host.classList.remove('invoice-breakdown-open')
      opts.onMade?.(made.row)
      changed()
      opts.done?.()
      if (via === 'email') {
        toast(made.email_error ?? `Invoice ${made.row.number} handed to email for ${made.row.to?.email ?? 'them'}`)
        opts.close()
        if (made.email_error) openInvoice(made.row.id, { done: opts.done })
        return
      }
      madeStep(made, via, seen.phone)
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 0)) sendKey = null
      // Something changed since it was looked at: said, and shown again as it is now (AGREED.md 4).
      if (e instanceof ApiError && e.code === 'changed') { load(failure(e)); return }
      if (e instanceof ApiError && (e.code === 'on_invoice' || e.code === 'invoice_setup_missing')) { refused(e); return }
      toast(failure(e))
      button.textContent = was
      for (const b of host.querySelectorAll('button')) (b as HTMLButtonElement).disabled = false
    }
  }

  // Made: one more tap opens WhatsApp or Messages with it written (a phone opens another app only straight from a tap).
  // Roundly counts it sent when that app opens; the record is waited for, and a failed one says so and can be tried
  // again, never closing as if it were sent (Astra C12, Sol C15; AGREED.md 7).
  const madeStep = (made: Made, via: Way, phone: string | null): void => {
    clear(host)
    host.append(el('p', {}, `Invoice ${made.row.number} is ready.`))
    const err = el('p', { class: 'form-error', role: 'alert' })
    const retry = el('button', { type: 'button', class: 'btn-secondary' }, 'Try again') as HTMLButtonElement
    retry.hidden = true
    let recordKey = crypto.randomUUID()
    // The number the link was written for: the server compares it when recording, so a number corrected on another phone
    // since is not counted as sent to (Astra's final review F2). Messages still opens at once from the tap.
    let phoneNow = phone
    const record = async (): Promise<void> => {
      err.textContent = ''
      retry.hidden = true
      try {
        await changeNow('POST', `/api/invoices/${made.row.id}/send`, { why: 'invoice', via, ...(via === 'sms' || via === 'whatsapp' ? { to_phone: phoneNow } : {}) }, recordKey)
        changed()
        opts.done?.()
        opts.close()
      } catch (e) {
        if (e instanceof ApiError && e.code === 'recipient_changed') {
          // Not counted: the number they have now, and a new link to it.
          recordKey = crypto.randomUUID()
          phoneNow = typeof e.body?.phone === 'string' ? e.body.phone : null
          err.textContent = failure(e)
          const next = phoneNow ? appHref(via as 'whatsapp' | 'sms', phoneNow, made.words) : null
          if (next && go) go.setAttribute('href', next)
          else if (go) go.replaceWith(el('p', { class: 'muted' }, 'No mobile number for them now. Open the invoice from their page to send it another way.'))
          return
        }
        err.textContent = "Couldn't mark it sent. Try again"
        retry.hidden = false
      }
    }
    retry.addEventListener('click', () => void record())
    const href = via === 'link' ? made.link : appHref(via as 'whatsapp' | 'sms', phone, made.words)
    let go: HTMLAnchorElement | null = null
    if (href) {
      go = el('a', { href, target: '_blank', rel: 'noopener', class: 'btn-primary invoice-go' }, via === 'link' ? 'Open the invoice' : via === 'whatsapp' ? 'Open WhatsApp to send it' : 'Open Messages to send it')
      if (via !== 'link') { const link=go;bindEmailSend(link,()=>({href:link.getAttribute('href')??'',name:made.row.to?.name,sent:()=>void record()})) }
      host.append(go)
      // Opened to save or share: sent once the owner says they sent it (their own email, a printed copy).
      if (via === 'link') {
        const sentIt = el('button', { type: 'button', class: 'btn-secondary invoice-go' }, "I've sent it to them")
        sentIt.addEventListener('click', () => void record())
        host.append(sentIt)
      }
    } else host.append(el('p', { class: 'muted' }, 'No mobile number for them. Open the invoice from their page to send it another way.'))
    host.append(err, retry, el('p', { class: 'muted invoice-note' }, via === 'link' ? 'It counts as sent once you say you sent it.' : onAPhone() ? `Roundly counts it sent when ${via === 'whatsapp' ? 'WhatsApp' : 'Messages'} opens with it.` : 'Send it in your app. If Did it send? appears, answer it here.'))
  }

  load()
}

// Today's Job done on the owner's phone (AGREED.md 14, Sol C12): "Send invoice" beside Undo, for a commercial customer
// invoiced each visit whose visit was saved not paid. Their page read just now, else as this phone last had it, a
// waiting switch laid over. Only an owner with Invoices on asks: a worker's Today asks nothing more.
export async function invoiceAfterJobLink(customerId: number, cleanId: number): Promise<UndoLink | null> {
  if (!invoicesHere()) return null
  let c: CommercialCustomer | null = null
  try {
    c = (await api<{ customer: CommercialCustomer }>(`/api/customers/${customerId}`)).customer ?? null
  } catch {
    c = readCache<{ customers?: CommercialCustomer[] }>('customers')?.data?.customers?.find((x) => x.id === customerId) ?? null
  }
  if (!invoiceAfterEachJob(c ?? { id: customerId })) return null
  return { act: () => openNewInvoice(customerId, { dayOf: cleanId }), label: 'Send invoice' }
}

// ---- An invoice as a PDF, shared or saved (design A, 26 Oct 2026) ------------------------------------------------
// Used by a new invoice once made and by one opened again from More, Invoices (Sol's PDF review 2): its PDF fetched,
// then a second tap shares or saves it (the phone opens its share menu only straight from a tap: Astra's PDF check 2);
// sent only when he says so (check 1). A failed fetch tries again on the same invoice, never making another (check 6).
const pdfFileName = (row: Invoice): string => `${row.kind === 'credit' ? 'Credit note' : 'Invoice'} ${row.number} ${row.to?.name ?? ''}`.normalize('NFKD').replace(/[^A-Za-z0-9 -]/g, '').replace(/\s+/g, ' ').trim().slice(0, 80) + '.pdf'
// The last prepared file's address, let go when another is made, and two minutes after (Sol's PDF review 6).
let pdfHref: string | null = null
const forgetPdf = (): void => { if (pdfHref) URL.revokeObjectURL(pdfHref); pdfHref = null }
export async function pdfFlow(host: HTMLElement, row: Invoice, words: string, mode: 'share' | 'download', opts: { close: () => void; done?: () => void; askSent: boolean; phone?: string | null; remind?: boolean }): Promise<void> {
  clear(host)
  host.classList.add('invoice-pdf-step')
  const title = el('p', { class: 'invoice-pdf-ready' }, `${row.kind === 'credit' ? 'Credit note' : 'Invoice'} ${row.number}: getting its PDF…`)
  const err = el('p', { class: 'form-error', role: 'alert' })
  const place = el('div', { class: 'invoice-pdf-go' })
  host.append(title, place, err)
  let after: HTMLElement | null = null
  const afterwards = (asked: boolean): void => {
    after?.remove()
    after = el('div', { class: 'invoice-pdf-after' })
    if (asked && opts.askSent) {
      after.append(el('p', { class: 'invoice-pdf-ask' }, `Did it go to ${row.to?.name ?? 'them'}?`))
      const fail = el('p', { class: 'form-error', role: 'alert' })
      const yes = el('button', { type: 'button', class: 'btn-primary' }, "I've sent it") as HTMLButtonElement
      const key = crypto.randomUUID()
      yes.addEventListener('click', () => {
        yes.disabled = true; fail.textContent = ''
        void changeNow('POST', `/api/invoices/${row.id}/send`, { why: 'invoice', via: 'link', pdf: true }, key).then(() => {
          changed(); opts.done?.(); toast(`${row.kind === 'credit' ? 'Credit note' : 'Invoice'} ${row.number} marked sent`); opts.close()
        }).catch(() => { yes.disabled = false; fail.textContent = "Couldn't mark it sent. Try again" })
      })
      const notYet = el('button', { type: 'button', class: 'btn-secondary' }, 'Not yet')
      notYet.addEventListener('click', () => { toast(`${row.number} is made, not sent yet. It's in More, Invoices, with Send PDF.`); opts.close() })
      after.append(el('div', { class: 'invoice-pdf-answers' }, yes, notYet), fail)
    } else if (asked && opts.remind) {
      // A reminder sent as its PDF counts as a reminder once he says it went, as a reminder by link does (Sonnet and
      // GPT-6.1, 26 Oct 2026: otherwise the invoice looks never chased). Looking, saving or a share put away counts nothing.
      after.append(el('p', { class: 'invoice-pdf-ask' }, `Did the reminder go to ${row.to?.name ?? 'them'}?`))
      const fail = el('p', { class: 'form-error', role: 'alert' })
      const yes = el('button', { type: 'button', class: 'btn-primary' }, "I've sent it") as HTMLButtonElement
      const key = crypto.randomUUID()
      yes.addEventListener('click', () => {
        yes.disabled = true; fail.textContent = ''
        void changeNow('POST', `/api/invoices/${row.id}/send`, { why: 'reminder', via: 'link', pdf: true }, key).then(() => {
          changed(); opts.done?.(); toast(`Reminder for ${row.number} counted`); opts.close()
        }).catch(() => { yes.disabled = false; fail.textContent = "Couldn't count the reminder. Try again" })
      })
      const notYet = el('button', { type: 'button', class: 'btn-secondary' }, 'Not yet')
      notYet.addEventListener('click', () => opts.close())
      after.append(el('div', { class: 'invoice-pdf-answers' }, yes, notYet), fail)
    }
    const copy = el('button', { type: 'button', class: 'link-button' }, 'Copy')
    copy.addEventListener('click', () => { void copyText(words, { button: copy, what: 'note' }) })
    after.append(el('div', { class: 'invoice-pdf-note-box' }, el('div', { class: 'invoice-pdf-note-head' }, el('span', {}, 'A note to send with it'), copy), el('p', {}, words)))
    if (!asked || (!opts.askSent && !opts.remind)) {
      const close = el('button', { type: 'button', class: 'link-button' }, opts.askSent ? 'Close: it stays made, not sent, in More, Invoices' : 'Close')
      close.addEventListener('click', () => opts.close())
      after.append(close)
    }
    host.append(after)
  }
  let file: File
  try {
    file = new File([await apiBlob(`/api/invoices/${row.id}/pdf`)], pdfFileName(row), { type: 'application/pdf' })
  } catch (e) {
    title.textContent = `${row.kind === 'credit' ? 'Credit note' : 'Invoice'} ${row.number} is made.`
    err.textContent = `Couldn't get its PDF: ${failure(e)}`
    const again = el('button', { type: 'button', class: 'btn-secondary' }, 'Try again')
    again.addEventListener('click', () => void pdfFlow(host, row, words, mode, opts))
    place.append(again)
    afterwards(false)
    return
  }
  title.textContent = `${row.kind === 'credit' ? 'Credit note' : 'Invoice'} ${row.number} is ready as a PDF.`
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean }
  const shareable = mode === 'share' && typeof nav.share === 'function' && !!nav.canShare?.({ files: [file] })
  if (shareable) {
    const go = el('button', { type: 'button', class: 'btn-primary invoice-pdf-send' }, 'Send PDF now') as HTMLButtonElement
    // The share menu can't be given who it is for, so Messages opens with To: empty (Liam, 26 Oct 2026, Joel's invoice:
    // "the send PDF as text didnt put his number in?"): try copying their number on the same tap, with a receipt.
    const number = opts.phone?.trim() || null
    const who = (row.to?.name ?? '').split(' ')[0] || 'their'
    go.addEventListener('click', () => {
      err.textContent = ''
      if (number) void copyText(number, { notice: `Paste ${who === 'their' ? 'their' : `${who}'s`} number in To.`, what: 'number' })
      // Straight from the tap: the file and the note, no link.
      nav.share({ files: [file], text: words }).then(() => { go.textContent = 'Send PDF again'; afterwards(true) }).catch((e: unknown) => {
        if ((e as Error)?.name !== 'AbortError') err.textContent = "The share menu didn't open. Use Download PDF and send it from there."
      })
    })
    place.append(go)
    if (number) place.append(el('p', { class: 'muted invoice-note invoice-pdf-number' }, `Messages and WhatsApp can't fill in who it's to. Roundly tries to copy ${who === 'their' ? 'their' : `${who}'s`} number (${number}) as you tap. When the green tick confirms it, tap To, then Paste. If copying fails, select the number here and copy it by hand.`))
  }
  // Download: a real link to the file, tapped (on the iPhone app it opens the phone's own share menu to save or send).
  forgetPdf()
  const href = pdfHref = URL.createObjectURL(file)
  window.setTimeout(() => { if (pdfHref === href) forgetPdf() }, 120_000)
  // On a computer with no share menu Send PDF saves the file, and says so (Sonnet and GPT-6.1's computer check).
  const download = el('a', { href, download: file.name, class: shareable ? 'btn-secondary invoice-pdf-download' : 'btn-primary invoice-pdf-send invoice-pdf-download' }, mode === 'share' && !shareable && onAPhone() ? 'Send PDF now' : 'Download PDF')
  download.addEventListener('click', () => { window.setTimeout(() => afterwards(true), 400) })
  place.append(download)
  if (mode === 'share' && !shareable) place.append(el('p', { class: 'muted invoice-note' }, onAPhone() ? 'Saves the PDF, then pick WhatsApp, Messages or Mail to send it.' : 'Saves the PDF to this computer: attach it to an email, or send it on WhatsApp Web.'))
  afterwards(false)
}

// The line under Send PDF: the phone's share menu, a computer's own one (a Mac's Safari has Mail and Messages), or, where
// there is none, the file saved to attach (Sonnet and GPT-6.1's computer check, 26 Oct 2026).
function pdfHint(): string {
  let share = false
  try {
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean }
    share = typeof nav.share === 'function' && !!nav.canShare?.({ files: [new File([''], 'invoice.pdf', { type: 'application/pdf' })] })
  } catch { share = false }
  if (onAPhone()) return share ? "Opens your phone's share menu: WhatsApp, Messages, Mail and more." : 'Saves the PDF, then pick WhatsApp, Messages or Mail to send it.'
  return share ? 'Opens the share menu: Mail, Messages and more.' : 'Saves the PDF to this computer, to attach to an email or send on WhatsApp Web.'
}

// ---- One invoice: what it says now, and what can be done with it --------------------------------------------------
export function openInvoice(id: number, opts: { phone?: string | null; done?: () => void } = {}): void {
  const s = sheet('Invoice')
  const title = el('h2', {}, 'Invoice')
  s.head.append(title)
  s.body.append(el('p', { class: 'muted' }, 'Opening…'))
  let phone: string | null = opts.phone ?? null
  const draw = (a: Answer): void => {
    clear(s.body)
    const inv = a.row
    title.textContent = `${inv.kind === 'credit' ? 'Credit note' : 'Invoice'} ${inv.number}`
    s.body.append(el('div', { class: 'invoice-state' }, stateChip(inv.state), el('span', { class: 'muted' }, inv.credited_by ? `Cancelled by credit note ${inv.credited_by}` : inv.sent_at ? `Sent ${dateWords(inv.sent_at)}${inv.sent_via ? ` by ${inv.sent_via === 'sms' ? 'text' : inv.sent_via === 'link' ? 'you, as a PDF or link' : inv.sent_via}` : ''}${inv.reminders ? ` · ${inv.reminders} reminder${inv.reminders === 1 ? '' : 's'}` : ''}` : 'Not sent yet')))
    s.body.append(customerInvoiceView(inv, inv.has_logo ? `/api/invoices/${inv.id}/logo` : null))
    const actions = el('div', { class: 'invoice-actions' })
    const grid = el('div', { class: 'invoice-grid' })
    const act = async (label: string, run: () => Promise<Answer | null | void>, failedWords?: string): Promise<void> => {
      for (const b of s.body.querySelectorAll('button')) (b as HTMLButtonElement).disabled = true
      try { const next = await run(); changed(); opts.done?.(); if (next) draw(next) } catch (e) {
        // Their number changed on another phone: said, and the links written again for the number they have now.
        if (e instanceof ApiError && e.code === 'recipient_changed') { phone = typeof e.body?.phone === 'string' ? e.body.phone : null; toast(failure(e)); draw(a); return }
        toast(failedWords ?? failure(e)); for (const b of s.body.querySelectorAll('button')) (b as HTMLButtonElement).disabled = false
      }
      void label
    }
    // A paused round sends nothing from Roundly: its words in place of every send (AGREED.md 7).
    const stop = cannotSend()
    const sendWhy = (why: 'invoice' | 'reminder' | 'receipt', label: string, main: boolean): void => {
      if (stop) return
      const words = a.words?.[why]
      if (a.email.ok) {
        const b = el('button', { type: 'button', class: main ? 'btn-primary' : 'btn-secondary' }, `${label} by email`)
        b.addEventListener('click', () => void act(label, async () => {
          const r = await changeNow<Answer>('POST', `/api/invoices/${inv.id}/send`, { why, via: 'email' })
          toast(`${label} handed to email for ${a.email.to}`)
          return r
        }))
        actions.append(b)
      }
      if (words) for (const [via, name] of [['whatsapp', 'WhatsApp'], ['sms', 'Text']] as const) {
        const href = appHref(via, phone, words)
        if (!href) continue
        const b = el('a', { href, target: '_blank', rel: 'noopener', class: !a.email.ok && main && via === 'whatsapp' ? 'btn-primary' : 'btn-secondary' }, `${label}: ${name}`)
        // Opening the app counts it sent; the record is waited for, and a failed one is said, never shown as sent (AGREED.md 7).
        // The number it was written for goes with the record (Astra's final review F2): changed since, not counted.
        bindEmailSend(b,()=>({href,name:inv.to?.name,sent:()=>void act(label, () => changeNow<Answer>('POST', `/api/invoices/${inv.id}/send`, { why, via, to_phone: phone }), "Couldn't mark it sent. Try again")}))
        grid.append(b)
      }
    }
    if (stop) actions.append(el('p', { class: 'invoice-note invoice-paused' }, stop))
    // As a PDF (design A; Sol's PDF review 2): an invoice made and not sent yet, or one to send again, shared or saved
    // from here, never made again. Not sent yet asks "Did it go?" after; a copy of one already sent does not.
    if (!stop && inv.state !== 'cancelled') {
      const why = inv.state === 'paid' ? 'receipt' : inv.state === 'sent' || inv.state === 'late' || inv.state === 'part_paid' ? 'reminder' : 'invoice'
      const words = a.pdf_words?.[why] ?? ''
      const unsent = !inv.sent_at
      const pdf = el('div', { class: 'invoice-pdf invoice-pdf-again' })
      const share = el('button', { type: 'button', class: unsent ? 'btn-primary invoice-pdf-send' : 'btn-secondary invoice-pdf-send' }, 'Send PDF')
      const save = el('button', { type: 'button', class: 'invoice-pdf-row' }, el('strong', {}, 'Download PDF'), el('small', {}, 'Save it and send it your own way'))
      const go = (mode: 'share' | 'download'): void => { void pdfFlow(s.body, inv, words, mode, { close: s.close, done: opts.done, askSent: unsent, phone, remind: !unsent && why === 'reminder' }) }
      share.addEventListener('click', () => go('share'))
      save.addEventListener('click', () => go('download'))
      pdf.append(share, el('div', { class: 'invoice-pdf-rows' }, save))
      actions.append(pdf)
    }
    if (inv.state === 'not_sent' && !stop) {
      sendWhy('invoice', 'Send', true)
      const myself = el('button', { type: 'button', class: 'btn-secondary' }, 'I sent it myself')
      myself.addEventListener('click', () => void act('Sent', () => changeNow<Answer>('POST', `/api/invoices/${inv.id}/send`, { why: 'invoice', via: 'link' })))
      grid.append(myself)
    }
    if (inv.state === 'credit') sendWhy('invoice', 'Send the credit note', true)
    if (inv.state === 'sent' || inv.state === 'late' || inv.state === 'part_paid') {
      const pay = el('div', { class: 'invoice-grid' })
      for (const [method, label] of [['bank', 'Paid by bank'], ['cash', 'Paid cash']] as const) {
        const b = el('button', { type: 'button', class: method === 'bank' ? 'btn-primary' : 'btn-secondary' }, label)
        b.addEventListener('click', () => void act('Mark paid', async () => { await markInvoicePaid(inv, method); return reread(inv.id) }))
        pay.append(b)
      }
      if (inv.part_paid && inv.remaining_pence != null) actions.append(el('p', { class: 'muted invoice-note' }, `Part paid: ${money(inv.remaining_pence)} still to pay.`))
      actions.append(pay)
      sendWhy('reminder', 'Send a reminder', false)
    }
    if (inv.state === 'paid' && inv.kind !== 'credit') sendWhy('receipt', 'Send receipt', true)
    if (a.link) {
      const open = el('button', { type: 'button', class: 'btn-secondary' }, 'Open to save or share')
      open.addEventListener('click', () => window.open(a.link!, '_blank', 'noopener'))
      grid.append(open)
    }
    if (grid.children.length) actions.append(grid)
    // Putting it right: cancel while nothing of it is paid, a credit note once something is.
    const fix = el('div', { class: 'invoice-fix' })
    // Never sent: cancelled whatever was paid since, never a credit note for an invoice they never saw (the recheck's S3).
    if (inv.kind !== 'credit' && inv.state !== 'cancelled' && inv.sent_at && (inv.state === 'paid' || inv.part_paid)) {
      const credit = el('button', { type: 'button', class: 'link-button' }, 'Send a credit note')
      credit.addEventListener('click', () => void act('Credit note', async () => {
        const r = await changeNow<Answer>('POST', `/api/invoices/${inv.id}/credit`, { version: inv.version })
        toast(`Credit note ${r.row.number} made. Open it to send it to them.`)
        return reread(inv.id).catch(() => r)
      }))
      fix.append(credit)
    } else if (inv.kind !== 'credit' && inv.state !== 'cancelled') {
      const cancel = el('button', { type: 'button', class: 'link-button' }, `Cancel ${inv.number}`)
      cancel.addEventListener('click', () => void act('Cancel', async () => {
        const r = await patchInvoice(inv.id, { version: inv.version, cancel: true }, `Cancel ${inv.number}`)
        toast(`${inv.number} cancelled. Its visits can go on a new invoice.`)
        return r
      }))
      fix.append(cancel)
    }
    if (a.link || inv.state !== 'cancelled') {
      const linkSwitch = el('button', { type: 'button', class: 'link-button' }, a.link ? 'Switch its link off' : 'Switch its link back on')
      linkSwitch.addEventListener('click', () => void act('Link', () => patchInvoice(inv.id, { version: inv.version, link_off: !!a.link }, a.link ? `Link off: ${inv.number}` : `Link on: ${inv.number}`)))
      fix.append(linkSwitch)
    }
    s.body.append(actions, fix)
  }
  void api<Answer>(`/api/invoices/${id}`).then(async (a) => { phone = await phoneOf(a.row.customer_id, opts.phone); draw(a) }).catch((e) => { clear(s.body); s.body.append(el('p', {}, failure(e))) })
}

// The invoice as it is now, and a change to it (cancel, its link) saved the app's usual way.
const reread = (id: number): Promise<Answer> => api<Answer>(`/api/invoices/${id}`)
async function patchInvoice(id: number, body: Record<string, unknown>, label: string): Promise<Answer | null> {
  const ack = await mutate({ label, method: 'PATCH', path: `/api/invoices/${id}`, body })
  if (ack.status === 0) { toast('Saved on this phone: it goes when there is signal.'); return null }
  if (!ack.ok) throw new Error(ack.error ?? 'It did not save. Try again.')
  return reread(id)
}

// Mark an invoice's unpaid visits paid by bank: one visit as the visit's own save, two or more all at once (the bank
// screen's way), so a closed app never leaves half of it marked.
async function markInvoicePaid(inv: Invoice, method: 'bank' | 'cash'): Promise<void> {
  const data = await api<{ cleans: { id: number; version: number; payment_status: string; state: string; deleted_at?: string | null }[] }>(`/api/customers/${inv.customer_id}`)
  const ids = new Set(inv.lines.map((l) => l.clean_id))
  const open = data.cleans.filter((c) => ids.has(c.id) && c.payment_status !== 'paid' && c.state === 'done')
  if (!open.length) return
  if (method === 'cash' || open.length === 1) {
    // Each visit as its own save (cash is recorded visit by visit, as the Paid buttons do).
    for (const c of open) {
      const ack = await mutate({ label: `${inv.number} paid`, method: 'PATCH', path: `/api/cleans/${c.id}`, body: { version: c.version, payment_status: 'paid', payment_method: method, chased: 0 } })
      if (!ack.ok && ack.status !== 0) throw new Error(ack.error ?? 'It did not save. Try again.')
    }
  } else {
    // By bank, all at once, twelve at a time (the bank tick-off's own save); its reference is this one marking.
    const stamp = Date.now().toString(36)
    for (let i = 0; i < open.length; i += 12) {
      const chunk = open.slice(i, i + 12)
      if (chunk.length === 1) {
        const ack = await mutate({ label: `${inv.number} paid`, method: 'PATCH', path: `/api/cleans/${chunk[0]!.id}`, body: { version: chunk[0]!.version, payment_status: 'paid', payment_method: 'bank', chased: 0 } })
        if (!ack.ok && ack.status !== 0) throw new Error(ack.error ?? 'It did not save. Try again.')
      } else {
        try { await changeNow('POST', '/api/bank-refs/apply', { paid_ref: `Invoice ${inv.number} marked paid ${stamp}-${i}`, visits: chunk.map((c) => ({ id: c.id, version: c.version })) }) }
        catch (e) { throw e instanceof ApiError && e.status === 409 ? new Error('Some of its visits changed. Open the invoice again and mark it paid.') : e }
      }
    }
  }
  toast(`${inv.number} paid`)
}

// Owed's invoice card (Liam, 26 Oct 2026: Joel's invoice "displays differently to regular customers ... there's no quick
// way like all the other customers"; https://claude.ai/artifact/9NridJXtvQsvtS9Z4z9XFo): Cash or Bank marks the whole
// invoice paid, as Mark paid in the invoice does, and hands back its Undo: each visit it marked put back as it was.
// On a round that asks what cash was taken (money brought from Squeegee), the cash is spread over its visits oldest first:
// each takes what is still to pay on it, the last takes the rest (more than the invoice is kept as credit, as Owed's own
// Cash does), and a visit nothing is left for stays owed. Each visit is its own save, as Owed's Paid buttons are, so a
// failure half way says so and the Undo puts back only what was marked (Sonnet's check, 26 Oct 2026). Undo reverses the
// money too where the round keeps it (payment-undo), else puts each visit's payment and chase back as they were.
export interface InvoicePayResult { number: string; undo: () => Promise<void>; failed: string | null; marked: number; stillToPay: number }
export async function payInvoiceFromOwed(id: number, method: 'bank' | 'cash', takenPence: number | null = null): Promise<InvoicePayResult> {
  const inv = (await reread(id)).row
  type V = { id: number; version: number; date: string; amount_pence: number | null; payment_status: string; payment_method: string | null; state: string; chased?: number | null; from_credit_pence?: number | null; paid_in_pence?: number | null }
  const data = await api<{ cleans: V[] }>(`/api/customers/${inv.customer_id}`)
  const ids = new Set(inv.lines.map((l) => l.clean_id))
  const open = data.cleans.filter((c) => ids.has(c.id) && c.payment_status !== 'paid' && c.state === 'done').sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
  const dueOf = (c: V): number => Math.max(0, toCollect(c as Parameters<typeof toCollect>[0]) ?? c.amount_pence ?? 0)
  const totalDue = open.reduce((n, c) => n + dueOf(c), 0)
  const marked: { id: number; status: string; method: string | null; chased: number; effect: string | null }[] = []
  let left = takenPence
  let failed: string | null = null
  for (const [i, c] of open.entries()) {
    let take: number | undefined
    if (method === 'cash' && left !== null) { take = i === open.length - 1 ? left : Math.min(left, dueOf(c)); left -= take }
    if (take === 0) continue
    const ack = await mutate({ label: `${inv.number} paid`, method: 'PATCH', path: `/api/cleans/${c.id}`,
      body: { version: c.version, payment_status: 'paid', payment_method: method, chased: 0, ...(take !== undefined ? { taken_pence: take } : {}) } })
    if (!ack.ok && ack.status !== 0) { failed = ack.error ?? 'It did not save. Try again.'; break }
    const effect = (ack.row as { _payment_effect?: { forward_key?: string } } | undefined)?._payment_effect?.forward_key ?? null
    marked.push({ id: c.id, status: c.payment_status, method: c.payment_method, chased: c.chased ?? 0, effect })
  }
  return {
    number: inv.number,
    failed,
    marked: marked.length,
    stillToPay: takenPence === null ? 0 : Math.max(0, totalDue - takenPence),
    undo: async () => {
      const now = await api<{ cleans: { id: number; version: number; payment_status: string; payment_method: string | null }[] }>(`/api/customers/${inv.customer_id}`)
      for (const m of marked) {
        if (m.effect) { await mutate({ label: `Undo ${inv.number} paid`, method: 'POST', path: `/api/cleans/${m.id}/payment-undo`, body: { forward_key: m.effect } }); continue }
        const c = now.cleans.find((x) => x.id === m.id)
        // Changed since (another phone put the payment right): left as it is now.
        if (!c || c.payment_status !== 'paid' || c.payment_method !== method) continue
        await mutate({ label: `Undo ${inv.number} paid`, method: 'PATCH', path: `/api/cleans/${c.id}`, body: { version: c.version, payment_status: m.status, payment_method: m.method, chased: m.chased } })
      }
    },
  }
}

// ---- "Already paid?" before an invoice for a visit with no payment recorded ------------------------------------------
export function invoiceVisit(visit: { id: number; customer_id: number; version?: number; payment_status?: string | null; customer_phone?: string | null }, done?: () => void): void {
  if (visit.payment_status !== 'unknown') { openNewInvoice(visit.customer_id, { cleanIds: [visit.id] }, { phone: visit.customer_phone, done }); return }
  const s = sheet('Already paid?')
  s.head.append(el('h2', {}, 'Already paid?'))
  s.body.append(el('p', {}, 'No payment is recorded for this visit. An invoice asks them for the money, so if they have paid, mark it paid instead.'))
  const go = el('button', { type: 'button', class: 'btn-primary' }, 'Not paid yet: make the invoice')
  go.addEventListener('click', () => { s.close(); openNewInvoice(visit.customer_id, { cleanIds: [visit.id] }, { phone: visit.customer_phone, done }) })
  const paid = el('div', { class: 'invoice-grid' })
  for (const [method, label] of [['cash', 'They paid cash'], ['bank', 'They paid by bank']] as const) {
    const b = el('button', { type: 'button', class: 'btn-secondary' }, label)
    b.addEventListener('click', () => {
      s.close()
      if (visit.version === undefined) { toast('Mark it paid on the visit.'); return }
      void mutate({ label: 'Paid', method: 'PATCH', path: `/api/cleans/${visit.id}`, body: { version: visit.version, payment_status: 'paid', payment_method: method, chased: 0 } })
        .then((ack) => { if (ack.ok || ack.status === 0) { toast(method === 'cash' ? 'Marked paid cash' : 'Marked paid by bank'); changed(); done?.() } else toast(ack.error ?? 'It did not save. Try again.') })
    })
    paid.append(b)
  }
  s.body.append(el('div', { class: 'invoice-actions' }, go, paid))
}

// ---- Which invoice each visit is on (the visit's details) ----------------------------------------------------------
export async function invoicesForVisits(ids: number[]): Promise<Record<string, { id: number; number: string; state: InvoiceState }>> {
  if (!invoicesHere() || !ids.length) return {}
  try { return (await api<{ on: Record<string, { id: number; number: string; state: InvoiceState }> }>(`/api/invoices?cleans=${ids.join(',')}`)).on } catch { return {} }
}

// ---- A customer's page: their Invoices ------------------------------------------------------------------------------
interface CardCustomer { id: number; name: string; version: number; phone?: string | null; commercial?: number | null; invoice_mode?: string | null; invoice_to_name?: string | null; invoice_to_email?: string | null; invoice_to_address?: string | null; invoice_po?: string | null }
interface CardVisit { id: number; version?: number; date: string; state: string; amount_pence: number | null; payment_status?: string | null; carryover?: number | null; deleted_at?: string | null }

export function customerInvoicesCard(c: CardCustomer, visits: CardVisit[], reload: () => void): HTMLElement {
  const card = el('section', { class: 'cust-past-box invoices-card', 'aria-label': 'Invoices' }, el('h3', {}, 'Invoices'))
  card.append(commercialSwitch(c, reload))
  const body = el('div', { class: 'invoices-card' }, el('p', { class: 'muted' }, 'Loading…'))
  card.append(body)
  const save = (fields: Record<string, unknown>, label: string): void => {
    void mutate({ label: `${label}: ${c.name}`, method: 'PATCH', path: `/api/customers/${c.id}`, body: { version: c.version, ...fields }, onConflict: reload })
      .then((ack) => { if (ack.ok || ack.status === 0) reload(); else toast(ack.error ?? 'It did not save. Try again.') })
  }
  // What is ready to invoice can fail on its own: their invoices still show (Sonnet).
  void Promise.all([api<{ invoices: Invoice[] }>(`/api/invoices?customer=${c.id}`), readyToInvoice(c.id).catch(() => null)]).then(([{ invoices }, readyOrNull]) => {
    const ready = readyOrNull ?? { monthly: false, visits: [], months: [] }
    clear(body)
    const monthly = c.invoice_mode === 'month'
    const seg = el('div', { class: 'invoices-seg', role: 'group', 'aria-label': 'How they are invoiced' })
    for (const [mode, label] of [['visit', 'Each visit'], ['month', 'Once a month']] as const) {
      const b = el('button', { type: 'button', 'aria-pressed': String(monthly === (mode === 'month')) }, label)
      b.addEventListener('click', () => { if (monthly !== (mode === 'month')) save({ invoice_mode: mode }, 'Invoices') })
      seg.append(b)
    }
    body.append(seg)
    const toWords = c.invoice_to_name || c.invoice_to_email ? [c.invoice_to_name, c.invoice_to_email, c.invoice_po ? `Order no. ${c.invoice_po}` : ''].filter(Boolean).join(' · ') : `${c.name}, at their address`
    const change = el('button', { type: 'button', class: 'link-button' }, 'Change')
    change.addEventListener('click', () => openInvoiceTo(c, save))
    body.append(el('div', { class: 'invoices-card-row' }, el('span', {}, el('span', { class: 'muted' }, 'Invoice to: '), toWords), change))
    // Done, priced visits not on a live invoice yet: the server's rule (readyFor), never worked out from this page's
    // newest invoices (Sonnet and Astra: an old month the server had closed was offered again).
    const open = ready.visits.map((r) => ({ ...r, payment_status: r.payment_status, version: r.version }))
    if (monthly) {
      for (const month of ready.months.slice(0, 3)) {
        const m = month.period
        const send = el('button', { type: 'button', class: 'btn-primary' }, `Send ${monthName(m)}'s`)
        send.disabled = month.over || month.to_pay_pence <= 0
        send.addEventListener('click', () => openNewInvoice(c.id, { month: m }, { phone: c.phone ?? null, done: reload }))
        body.append(el('div', { class: 'invoices-month' }, el('span', {}, el('strong', {}, monthName(m)), el('br'), el('small', { class: 'muted' }, `${month.visits} visit${month.visits === 1 ? '' : 's'} · ${money(month.to_pay_pence)}${month.over ? ' · more than 31: send it from More, Invoices' : ''}`)), send))
      }
      if (!readyOrNull) body.append(el('p', { class: 'muted' }, 'What is ready to invoice needs signal to show.'))
      else if (!ready.months.length) body.append(el('p', { class: 'muted' }, 'Nothing to invoice: no done visits waiting for an invoice.'))
    } else if (open.length) {
      body.append(el('p', { class: 'muted' }, 'Done visits not invoiced yet:'))
      for (const v of open.slice(0, 3)) {
        const b = el('button', { type: 'button', class: 'btn-secondary' }, `Invoice ${dateWords(v.date)} · ${money(v.amount_pence)}`)
        b.addEventListener('click', () => invoiceVisit({ id: v.id, customer_id: c.id, version: v.version, payment_status: v.payment_status ?? null, customer_phone: c.phone ?? null }, reload))
        body.append(b)
      }
    }
    if (invoices.length) {
      const list = el('div', { class: 'invoices-list' })
      for (const inv of invoices.slice(0, 12)) {
        const what = inv.kind === 'month' && inv.period ? monthName(inv.period) : inv.kind === 'credit' ? 'Credit note' : inv.period ? dateWords(inv.period) : ''
        const b = el('button', { type: 'button' }, el('span', {}, `${inv.number} · ${what}`, el('br'), el('small', {}, signed(inv.kind === 'credit' ? inv.total_pence : inv.subtotal_pence))), stateChip(inv.state))
        b.addEventListener('click', () => openInvoice(inv.id, { phone: c.phone ?? null, done: reload }))
        list.append(b)
      }
      body.append(list)
    }
  }).catch(() => { clear(body); body.append(el('p', { class: 'muted' }, 'Invoices need signal to show.')) })
  return card
}

// Commercial (0080; Liam, 20 Oct 2026; docs/plans/commercial/AGREED.md 1): "invoice them after each job", for the whole
// customer, every job of theirs. Saved at the version shown, with Undo; a change still waiting for signal shows as it
// will be, a refused one does not (isCommercial reads the phone's queue, AGREED.md 15). Shown whether or not their
// invoices can be read just now.
function commercialSwitch(c: CardCustomer, reload: () => void): HTMLElement {
  const on = isCommercial(c)
  const path = `/api/customers/${c.id}`
  const sw = el('button', { type: 'button', role: 'switch', class: `mb-switch invoice-commercial-switch${on ? ' mb-switch-on' : ''}`, 'aria-checked': String(on), 'aria-labelledby': `commercial-${c.id}` },
    el('span', { class: 'mb-switch-knob', 'aria-hidden': 'true' })) as HTMLButtonElement
  sw.addEventListener('click', () => {
    const to = on ? 0 : 1
    sw.disabled = true
    void mutate({ label: `Commercial: ${c.name}`, method: 'PATCH', path, body: { version: c.version, commercial: to }, onConflict: reload }).then((ack) => {
      if (ack.ok || ack.status === 0) {
        const waits = ack.ok ? '' : '. No signal: saved on this phone, it sends as soon as it can'
        undoPatch({ text: `${to ? `${c.name} is commercial: Roundly offers their invoice after each job` : `${c.name} is not commercial`}${waits}`, label: `Undo commercial: ${c.name}`, path, ack, version: c.version, body: { commercial: to ? 0 : 1 }, after: reload })
        reload()
      } else { sw.disabled = false; toast(ack.error ?? 'It did not save. Try again.') }
    })
  })
  const words = !on ? 'For a business: every job of theirs.' : c.invoice_mode === 'month' ? "Once a month: send their month's invoice from here." : 'After Done, Roundly offers their invoice.'
  return el('div', { class: 'invoices-card-row invoice-commercial' },
    el('span', { id: `commercial-${c.id}` }, el('strong', {}, 'Commercial: invoice them after each job'), el('br'), el('small', { class: 'muted' }, words)), sw)
}

// Who the invoice goes to, when it is not them: a company's accounts, with an order number.
function openInvoiceTo(c: CardCustomer, save: (fields: Record<string, unknown>, label: string) => void): void {
  const s = sheet('Invoice to')
  s.head.append(el('h2', {}, 'Invoice to'))
  s.body.append(el('p', { class: 'muted' }, `Leave these empty to invoice ${c.name} at their own address and email.`))
  const form = el('div', { class: 'invoice-setup' })
  const field = (id: string, label: string, value: string | null | undefined, hint = ''): HTMLInputElement => {
    const input = el('input', { id, ...(hint ? { placeholder: hint } : {}) }) as HTMLInputElement
    input.value = value ?? ''
    form.append(el('label', { for: id }, label), input)
    return input
  }
  const name = field('inv-to-name', 'Name', c.invoice_to_name, 'Accounts, Example Ltd')
  const email = field('inv-to-email', 'Email', c.invoice_to_email, 'accounts@example.com')
  const address = field('inv-to-address', 'Address', c.invoice_to_address)
  const po = field('inv-to-po', 'Order number (optional)', c.invoice_po)
  const ok = el('button', { type: 'button', class: 'btn-primary' }, 'Save')
  ok.addEventListener('click', () => {
    save({ invoice_to_name: name.value.trim() || null, invoice_to_email: email.value.trim() || null, invoice_to_address: address.value.trim() || null, invoice_po: po.value.trim() || null }, 'Invoice to')
    s.close()
  })
  form.append(ok)
  s.body.append(form)
}
